import { z } from "zod";

// User schemas
export const insertUserSchema = z.object({
  id: z.string().optional(),
  email: z.string().email(),
  name: z.string().min(1),
  username: z.string().min(1),
  password: z.string().min(6).optional(),
  created_at: z.date().optional(),
  updated_at: z.date().optional(),
});

export type User = {
  id: number;
  email: string;
  name: string;
  username: string;
  password?: string;
  created_at: Date;
  updated_at: Date;
  stripe_customer_id?: string;
};

export type InsertUser = z.infer<typeof insertUserSchema>;

// Client schemas
export const insertClientSchema = z.object({
  id: z.string().optional(),
  userId: z.number().optional(),
  name: z.string().min(1, 'Client name is required'),
  email: z.string().email().optional(),
  company: z.string().optional(),
  phone: z.string().optional(),
  address: z.string().optional(),
  billingContactName: z.string().optional().nullable(),
  billingContactEmail: z.string().email().optional().nullable(),
  billingAddress: z.string().optional().nullable(),
  billingCity: z.string().optional().nullable(),
  billingState: z.string().optional().nullable(),
  billingZip: z.string().optional().nullable(),
  billingCountry: z.string().optional().nullable(),
  created_at: z.date().optional(),
  updated_at: z.date().optional(),
});

export type Client = {
  id: number;
  userId: number;
  name: string;
  email?: string;
  company?: string;
  phone?: string;
  address?: string;
  billingContactName?: string | null;
  billingContactEmail?: string | null;
  billingAddress?: string | null;
  billingCity?: string | null;
  billingState?: string | null;
  billingZip?: string | null;
  billingCountry?: string | null;
  created_at: Date;
  updated_at: Date;
};

export type InsertClient = z.infer<typeof insertClientSchema>;

// Engagement schemas
export const engagementTypeEnum = z.enum(['hourly', 'fixed', 'retainer', 'daily']);

export function calculateEngagementStatus(engagement: any): string {
  if (!engagement) return 'inactive';
  if (engagement.endDate && new Date(engagement.endDate) < new Date()) {
    return 'completed';
  }
  if (engagement.startDate && new Date(engagement.startDate) > new Date()) {
    return 'upcoming';
  }
  return 'active';
}

export const insertEngagementSchema = z.object({
  id: z.string().optional(),
  userId: z.number().optional(),
  clientId: z.number().min(1, 'Client is required'),
  clientName: z.string().optional(), // For UI convenience
  projectName: z.string().min(1, 'Project name is required'),
  engagementType: engagementTypeEnum,
  hourlyRate: z.number().positive().optional().nullable(),
  projectAmount: z.number().positive().optional().nullable(),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().optional().nullable(),
  netTerms: z.number().default(30),
  description: z.string().optional(),
  status: z.string().optional(),
  created_at: z.date().optional(),
  updated_at: z.date().optional(),
});

export const updateEngagementSchema = insertEngagementSchema.partial();

export type Engagement = {
  id: number;
  userId: number;
  clientId: number;
  clientName?: string; // Joined field
  clientEmail?: string; // Joined field
  projectName: string;
  engagementType: 'hourly' | 'fixed' | 'retainer' | 'daily';
  hourlyRate?: number | null;
  projectAmount?: number | null;
  startDate: string;
  endDate?: string | null;
  netTerms: number;
  description?: string;
  status: string;
  created_at: Date;
  updated_at: Date;
};

export type InsertEngagement = z.infer<typeof insertEngagementSchema>;

// Time log schemas
export const insertTimeLogSchema = z.object({
  id: z.string().optional(),
  engagementId: z.number().min(1, 'Please select an engagement'),
  description: z.string().optional(),
  hours: z.number().min(0.25, 'Hours must be at least 0.25').max(24, 'Hours cannot exceed 24'),
  date: z.string().min(1, 'Date is required'),
  userId: z.number().optional(),
  created_at: z.date().optional(),
  updated_at: z.date().optional(),
});

export type InsertTimeLog = z.infer<typeof insertTimeLogSchema>;

export type TimeLog = {
  id: number;
  userId: number;
  engagementId: number;
  date: string;
  hours: number;
  description?: string;
  created_at: Date;
  updated_at: Date;
};

export type TimeLogWithEngagement = TimeLog & {
  billableAmount: number;
  clientName: string;
  projectName: string;
  hourlyRate: number;
};

// Invoice schemas
export const insertInvoiceSchema = z.object({
  id: z.string().optional(),
  engagementId: z.number().min(1, 'Please select an engagement'),
  invoiceNumber: z.string().min(1, 'Invoice number is required'),
  amount: z.number().positive(),
  status: z.enum(['draft', 'sent', 'paid', 'overdue']),
  dueDate: z.string().min(1, 'Due date is required'),
  created_at: z.date().optional(),
  updated_at: z.date().optional(),
});

export type InsertInvoice = z.infer<typeof insertInvoiceSchema>;

export type Invoice = {
  id: number;
  engagementId: number;
  invoiceNumber: string;
  amount: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue';
  dueDate: string;
  created_at: Date;
  updated_at: Date;
  // Additional properties used in the application
  clientName?: string;
  projectName?: string;
  issueDate?: string;
  totalAmount?: number;
  totalHours?: number;
  notes?: string;
  periodStart?: string;
  periodEnd?: string;
  netTerms?: number;
};

export type InvoiceWithLineItems = Invoice & {
  lineItems: Array<{
    id: string;
    description: string;
    quantity: number;
    rate: number;
    amount: number;
  }>;
  engagement?: {
    id: number;
    clientName: string;
    projectName: string;
  };
};

export type PasswordResetToken = {
  id: number;
  userId: number;
  token: string;
  expiresAt: Date;
  used: boolean;
  created_at: Date;
};

// Mock table exports for compatibility (deprecated, use server/schema.ts)
export const engagements = {};
export const clients = {};
export const timeLogs = {};
export const invoices = {};
export const users = {};
export const passwordResetTokens = {};